import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

/** Today's date as YYYY-MM-DD (input[type=date] value) */
export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const LISTING_URL_KEYS = [
  "page",
  "limit",
  "startDate",
  "endDate",
  "search",
  "status",
  "accountStatus",
  "sortBy",
  "sortDir",
  "role",
];

/**
 * Shared listing state: page, limit, date range, debounced search, status, sort.
 * Syncs filters to the URL (preserves unrelated query params).
 */
export function useListingState(defaults = {}) {
  const syncUrl = defaults.syncUrl !== false;
  const includeDates = defaults.includeDates !== false;
  const [searchParams, setSearchParams] = useSearchParams();
  const skipUrlWrite = useRef(true);

  const fromUrl = useCallback(
    (key, fallback = "") => {
      if (!syncUrl) return fallback;
      const v = searchParams.get(key);
      return v != null && v !== "" ? v : fallback;
    },
    [searchParams, syncUrl]
  );

  const initialDay = useMemo(
    () => defaults.startDate ?? todayISO(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [page, setPage] = useState(
    () => Number(fromUrl("page", defaults.page || 1)) || 1
  );
  const [limit, setLimitState] = useState(
    () => Number(fromUrl("limit", defaults.limit || 10)) || 10
  );
  const [startDate, setStartDateState] = useState(() =>
    includeDates ? fromUrl("startDate", initialDay) : ""
  );
  const [endDate, setEndDateState] = useState(() =>
    includeDates ? fromUrl("endDate", defaults.endDate ?? initialDay) : ""
  );
  const [search, setSearch] = useState(() => fromUrl("search", defaults.search || ""));
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    String(fromUrl("search", defaults.search || "")).trim()
  );
  const [status, setStatusState] = useState(() =>
    fromUrl("status", defaults.status || "")
  );
  const [accountStatus, setAccountStatusState] = useState(() =>
    fromUrl("accountStatus", defaults.accountStatus || "")
  );
  const [role, setRoleState] = useState(() => fromUrl("role", defaults.role || ""));
  const [sortBy, setSortBy] = useState(() =>
    fromUrl("sortBy", defaults.sortBy || "")
  );
  const [sortDir, setSortDir] = useState(() =>
    fromUrl("sortDir", defaults.sortDir || "desc")
  );

  useEffect(() => {
    const t = setTimeout(() => {
      const next = search.trim();
      setDebouncedSearch((prev) => {
        if (prev !== next) setPage(1);
        return next;
      });
    }, 450);
    return () => clearTimeout(t);
  }, [search]);

  // Write listing filters into URL (keep other params like service/label)
  useEffect(() => {
    if (!syncUrl) return;
    if (skipUrlWrite.current) {
      skipUrlWrite.current = false;
      // still write once so bookmarkable defaults appear
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const setOrDel = (key, value) => {
          if (value == null || value === "") next.delete(key);
          else next.set(key, String(value));
        };

        setOrDel("page", page);
        setOrDel("limit", limit);
        if (includeDates) {
          setOrDel("startDate", startDate);
          setOrDel("endDate", endDate);
        } else {
          next.delete("startDate");
          next.delete("endDate");
        }
        setOrDel("search", debouncedSearch || "");
        setOrDel("status", status || "");
        setOrDel("accountStatus", accountStatus || "");
        setOrDel("role", role || "");
        if (sortBy) {
          next.set("sortBy", sortBy);
          next.set("sortDir", sortDir || "desc");
        } else {
          next.delete("sortBy");
          next.delete("sortDir");
        }

        if (next.toString() === prev.toString()) return prev;
        return next;
      },
      { replace: true }
    );
  }, [
    syncUrl,
    includeDates,
    page,
    limit,
    startDate,
    endDate,
    debouncedSearch,
    status,
    accountStatus,
    role,
    sortBy,
    sortDir,
    setSearchParams,
  ]);

  const setLimit = useCallback((value) => {
    setLimitState(Number(value) || 10);
    setPage(1);
  }, []);

  const setStartDate = useCallback((value) => {
    setStartDateState(value);
    setPage(1);
  }, []);

  const setEndDate = useCallback((value) => {
    setEndDateState(value);
    setPage(1);
  }, []);

  const setStatus = useCallback((value) => {
    setStatusState(value);
    setPage(1);
  }, []);

  const setAccountStatus = useCallback((value) => {
    setAccountStatusState(value);
    setPage(1);
  }, []);

  const setRole = useCallback((value) => {
    setRoleState(value);
    setPage(1);
  }, []);

  const toggleSort = useCallback((key) => {
    if (!key) return;
    setSortBy((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return key;
    });
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    const day = todayISO();
    setPage(1);
    setLimitState(Number(defaults.limit) || 10);
    setSearch("");
    setDebouncedSearch("");
    setStatusState(defaults.status || "");
    setAccountStatusState(defaults.accountStatus || "");
    setRoleState(defaults.role || "");
    if (includeDates) {
      setStartDateState(defaults.startDate ?? day);
      setEndDateState(defaults.endDate ?? defaults.startDate ?? day);
    } else {
      setStartDateState("");
      setEndDateState("");
    }
    setSortBy(defaults.sortBy || "");
    setSortDir(defaults.sortDir || "desc");
  }, [
    defaults.limit,
    defaults.startDate,
    defaults.endDate,
    defaults.sortBy,
    defaults.sortDir,
    defaults.status,
    defaults.accountStatus,
    defaults.role,
    includeDates,
  ]);

  const hasActiveFilters = useMemo(() => {
    const day = todayISO();
    const datesDirty =
      includeDates &&
      (startDate !== (defaults.startDate ?? day) ||
        endDate !== (defaults.endDate ?? defaults.startDate ?? day));
    return Boolean(
      debouncedSearch ||
        status ||
        accountStatus ||
        role ||
        datesDirty ||
        (sortBy && sortBy !== (defaults.sortBy || "")) ||
        (sortBy === (defaults.sortBy || "") &&
          sortDir !== (defaults.sortDir || "desc") &&
          sortBy) ||
        page !== 1 ||
        limit !== (Number(defaults.limit) || 10)
    );
  }, [
    includeDates,
    startDate,
    endDate,
    debouncedSearch,
    status,
    accountStatus,
    role,
    sortBy,
    sortDir,
    page,
    limit,
    defaults.startDate,
    defaults.endDate,
    defaults.sortBy,
    defaults.sortDir,
    defaults.limit,
  ]);

  const apiSortBy = sortBy || undefined;

  const query = useMemo(() => {
    const q = {
      page,
      limit,
      search: debouncedSearch || undefined,
      status: status || undefined,
      accountStatus: accountStatus || undefined,
      role: role || undefined,
      sortBy: apiSortBy,
      sortDir: apiSortBy ? sortDir : undefined,
    };
    if (includeDates) {
      q.startDate = startDate || undefined;
      q.endDate = endDate || undefined;
    }
    return q;
  }, [
    page,
    limit,
    includeDates,
    startDate,
    endDate,
    debouncedSearch,
    status,
    accountStatus,
    role,
    apiSortBy,
    sortDir,
  ]);
  return {
    page,
    setPage,
    limit,
    setLimit,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    search,
    setSearch,
    debouncedSearch,
    status,
    setStatus,
    accountStatus,
    setAccountStatus,
    role,
    setRole,
    sortBy,
    sortDir,
    toggleSort,
    clearFilters,
    hasActiveFilters,
    query,
    includeDates,
    listingUrlKeys: LISTING_URL_KEYS,
  };
}

/** Showing X–Y of Z helper */
export function listingRange(page, limit, total) {
  const t = Number(total) || 0;
  if (t <= 0) return { from: 0, to: 0, total: 0 };
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, t);
  return { from, to, total: t };
}
